<template>
  <CDialog v-model="isOpen">
    <header class="dialog-header align-items-start">
      <div class="flex-fill">
        <h1 class="h4 mrgv0">
          {{ title }}
        </h1>
        <h2 v-if="group && group.name" class="h5 text-alt mrgt--">
          <template v-if="user">
            {{ user.user.label }}
            ({{ group.name }})
          </template>

          <template v-else>
            {{ group.name }}
          </template>
        </h2>
      </div>

      <Btn variant="icon" @click="isOpen = !isOpen">
        <Icon id="close" :scale="0.75" />
      </Btn>
    </header>

    <main class="dialog-body">
      <ValidationObserver>
        <Form @passed="submit">
          <Grid multiline>
            <GridItem :widths="{ default: '1/1' }">
              <FormGroup v-bind="fields.user">
                <CallableUsers
                  v-model="form.user"
                  :name="fields.user.name"
                  required
                />
              </FormGroup>
            </GridItem>

            <GridItem :widths="{ default: '1/1' }">
              <FormGroup v-bind="fields.startDate">
                <Datepicker v-model="form.startDate" mode="dateTime" />
              </FormGroup>
            </GridItem>

            <GridItem :widths="{ default: '1/1' }">
              <FormGroup v-bind="fields.endDate">
                <Datepicker v-model="form.endDate" mode="dateTime" />
              </FormGroup>
            </GridItem>
          </Grid>

          <ListInline align="center" size="small" class="mrgt+">
            <li>
              <Btn variant="outline" @click="isOpen = false">
                {{ $t('common.cancel') }}
              </Btn>
            </li>
            <li>
              <Btn type="submit" variant="primary">
                <div class="flex">
                  <Loader v-if="loading" size="small" negative />
                  <template v-else>
                    {{ $t(`common.${user ? 'edit' : 'add'}`) }}
                  </template>
                </div>
              </Btn>
            </li>
          </ListInline>
        </Form>
      </ValidationObserver>
    </main>
  </CDialog>
</template>

<script>
import eventBus from '@/eventBus';
import { ValidationObserver } from 'vee-validate';
import { parseDate } from '@/helpers/dates';

import CallableUsers from '@/components/callable-users/CallableUsers';
import Loader from '@/components/ui/Loader';

export default {
  components: {
    ValidationObserver,
    CallableUsers,
    Loader,
  },

  data() {
    return {
      isOpen: false,
      loading: false,
      group: null,
      user: null,
      form: {
        user: null,
        startDate: null,
        endDate: null,
      },
    };
  },

  computed: {
    title() {
      return this.$t(`planification.users.${this.user ? 'edit' : 'add'}.title`);
    },

    fields() {
      return {
        user: {
          name: 'user',
          label: this.$t('planification.users.fields.user.label'),
          rules: 'required',
          mode: 'passive',
          required: true,
          row: true,
        },
        startDate: {
          name: 'start-date',
          legend: this.$t('planification.users.fields.start-datetime.label'),
          mode: 'passive',
          row: true,
        },
        endDate: {
          name: 'end-date',
          legend: this.$t('planification.users.fields.end-datetime.label'),
          mode: 'passive',
          row: true,
        },
      };
    },
  },

  watch: {
    isOpen(isOpen) {
      if (!this.isOpen) {
        this.group = null;
        this.user = null;
        this.form.user = null;
        this.form.startDate = null;
        this.form.endDate = null;
      }
    },
  },

  mounted() {
    eventBus.$on('togglePlanificationUserAddOrUpdate', this.handleToggle);
  },

  beforeDestroy() {
    eventBus.$off('togglePlanificationUserAddOrUpdate', this.handleToggle);
  },

  methods: {
    handleToggle({ group, user }) {
      this.group = group;
      if (user) {
        this.user = user;
        this.form.user = user.user;
        this.form.startDate = user.start_datetime;
        this.form.endDate = user.end_datetime;
      }

      this.isOpen = !this.isOpen;
    },

    submit() {
      if (this.user) {
        this.handleUpdateUser();
      } else {
        this.handleAddUser();
      }
    },

    handleAddUser() {
      this.loading = true;

      const user = {
        username: this.form.user.username,
        start_datetime: this.form.startDate,
        end_datetime: this.form.endDate,
        parent_group: {
          id: this.group.id,
          name: this.group.name,
          username: this.group.username,
        },
        user: this.form.user,
      };

      this.$store
        .dispatch('planification/addGroupUser', user)
        .then(response => {
          this.isOpen = false;
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            title: this.$t('common.error'),
            text: error.message || error,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleUpdateUser() {
      this.loading = true;

      const user = {
        id: this.user.id,
        username: this.form.user.username,
        start_datetime: this.form.startDate,
        end_datetime: this.form.endDate,
        user: this.form.user,
        parent_group: this.group,
      };

      this.$store
        .dispatch('planification/updateGroupUser', user)
        .then(response => {
          this.isOpen = false;
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            title: this.$t('common.error'),
            text: error.message || error,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
